import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Trash2,
  Search,
  BookOpen,
  Loader2,
  Sparkles,
  FileText,
  PenLine,
  Palette,
  Send,
  Mic,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Markdown from "@/components/Markdown";
import VoiceRecorder from "@/components/VoiceRecorder";
import QuickActionBar from "@/components/QuickActionBar";

// Stable reference — declared once at module scope so React doesn't see a new array on every render.
const QUICKBAR_NO_EXCLUDE = [];

export default function KnowledgeHub() {
  return (
    <Layout title="Knowledge Hub">
      <QuickActionBar exclude={QUICKBAR_NO_EXCLUDE} />
      <Tabs defaultValue="library">
        <TabsList className="rounded-full bg-muted p-1">
          <TabsTrigger value="library" className="rounded-full px-4" data-testid="tab-library">
            <BookOpen className="h-3.5 w-3.5 mr-1.5" /> Library
          </TabsTrigger>
          <TabsTrigger value="search" className="rounded-full px-4" data-testid="tab-search">
            <Search className="h-3.5 w-3.5 mr-1.5" /> Semantic Search
          </TabsTrigger>
          <TabsTrigger value="ask" className="rounded-full px-4" data-testid="tab-ask">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Ask AI
          </TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="mt-5">
          <Library />
        </TabsContent>
        <TabsContent value="search" className="mt-5">
          <SemanticSearch />
        </TabsContent>
        <TabsContent value="ask" className="mt-5">
          <AskAI />
        </TabsContent>
      </Tabs>
    </Layout>
  );
}

function Library() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ocr/documents");
      setDocs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) {
      refresh();
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/ocr/search?q=${encodeURIComponent(q.trim())}`);
      setDocs(data);
    } finally {
      setSearching(false);
    }
  };

  const open_ = async (id) => {
    try {
      const { data } = await api.get(`/ocr/documents/${id}`);
      setSelected(data);
    } catch {
      toast.error("Failed to open document");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/ocr/documents/${id}`);
    if (selected?.document_id === id) setSelected(null);
    refresh();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2 space-y-4">
        <form onSubmit={search} className="flex gap-2" data-testid="kh-search-form">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your documents…"
              className="pl-10 h-11 rounded-full"
              data-testid="kh-search-input"
            />
          </div>
          <Button type="submit" disabled={searching} className="h-11 rounded-full" data-testid="kh-search-button">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
        </form>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : docs.length === 0 ? (
          <div className="tb-card p-8 text-center text-muted-foreground">
            <BookOpen className="h-8 w-8 mx-auto opacity-50" />
            <p className="mt-3 font-display">No documents yet.</p>
            <p className="text-xs mt-1">Use Document OCR to scan and save.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="kh-doc-list">
            {docs.map((d) => (
              <button
                key={d.document_id}
                onClick={() => open_(d.document_id)}
                className={`w-full text-left rounded-2xl border p-4 transition ${
                  selected?.document_id === d.document_id
                    ? "border-violet-300 bg-violet-50/60 dark:bg-violet-500/10 dark:border-violet-500/30"
                    : "border-border bg-card hover:bg-muted"
                }`}
                data-testid={`kh-doc-${d.document_id}`}
              >
                <p className="text-sm font-medium truncate">{d.title}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {d.detected_language} · {new Date(d.created_at).toLocaleDateString()}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="lg:col-span-3 tb-card p-6 min-h-[480px]" data-testid="kh-doc-view">
        {!selected ? (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
            <div>
              <BookOpen className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3 font-display">Select a document to view.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{selected.detected_language}</p>
                <h2 className="mt-1 font-display text-xl tracking-tight font-medium">{selected.title}</h2>
              </div>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(selected.document_id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Summary</p>
              <Markdown className="mt-2">{selected.summary}</Markdown>
            </div>
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Extracted text</p>
              <pre className="mt-2 whitespace-pre-wrap text-xs bg-muted rounded-xl p-4 font-mono">
{selected.extracted_text}
              </pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SemanticSearch() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.post("/knowledge/search", { query: q, top_k: 8 });
      setData(data);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="flex gap-2" data-testid="kh-semantic-form">
        <div className="flex-1 relative">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search across documents, lesson plans, notes & worksheets…"
            className="pl-10 h-11 rounded-full"
            data-testid="kh-semantic-input"
          />
        </div>
        <Button type="submit" disabled={loading || !q.trim()} className="h-11 rounded-full" data-testid="kh-semantic-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </Button>
      </form>
      <p className="text-[11px] text-muted-foreground">
        Powered by BM25 lexical retrieval — ready to swap to vector embeddings.
      </p>

      {data?.results?.length === 0 && (
        <p className="text-sm text-muted-foreground py-12 text-center">No matches found in your library.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="kh-search-results">
        {data?.results?.map((r) => (
          <ResultCard key={`${r.source}-${r.record_id}`} r={r} />
        ))}
      </div>
    </div>
  );
}

function AskAI() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const submit = async () => {
    if (!q.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const { data } = await api.post("/knowledge/ask", { question: q, top_k: 5 });
      setData(data);
    } catch {
      toast.error("Question failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="tb-card p-5" data-testid="kh-ask-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> Ask anything from your library
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          AI synthesizes an answer with citations from your saved documents, lesson plans, MCQs, notes, and presentations.
        </p>
        <div className="mt-3 flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="What did I cover about photosynthesis last month?"
            className="h-11 rounded-full"
            data-testid="kh-ask-input"
          />
          <Button
            variant="outline"
            className="h-11 w-11 p-0 rounded-full"
            onClick={() => setVoiceOpen(true)}
            aria-label="Voice"
          >
            <Mic className="h-4 w-4" />
          </Button>
          <Button onClick={submit} disabled={loading || !q.trim()} className="h-11 rounded-full" data-testid="kh-ask-send">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="tb-card p-8 text-center text-muted-foreground" data-testid="kh-ask-loading">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="mt-3 font-display">Reading your library…</p>
        </div>
      )}

      {data && (
        <div className="space-y-5" data-testid="kh-ask-result">
          <div className="tb-card p-6">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Answer</p>
            <Markdown className="mt-2">{data.answer}</Markdown>
          </div>
          {data.citations?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Citations</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.citations.map((r, idx) => (
                  <ResultCard key={`${r.source}-${r.record_id}`} r={r} index={idx + 1} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <VoiceRecorder
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        onResult={(t) => setQ((v) => (v ? `${v} ${t}` : t))}
      />
    </div>
  );
}

function ResultCard({ r, index }) {
  const navigate = useNavigate();
  const Icon =
    r.source === "document" ? FileText :
    r.source === "generation" ? PenLine :
    Palette;

  const sourceLabel =
    r.source === "document" ? "Document" :
    r.source === "generation" ? "AI generation" :
    "Content Studio";

  return (
    <button
      onClick={() => r.href && navigate(r.href)}
      className="tb-card p-4 text-left flex flex-col gap-2"
      data-testid={`kh-result-${r.source}-${r.record_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
          {index ? <span className="rounded-full bg-violet-500 text-white px-1.5">[{index}]</span> : null}
          <Icon className="h-3 w-3" /> {sourceLabel}
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">score {r.score.toFixed(2)}</span>
      </div>
      <p className="font-medium text-sm">{r.title}</p>
      <p className="text-xs text-muted-foreground line-clamp-3">{r.snippet}</p>
    </button>
  );
}
